// `(metric …)` tile op (kpi variant only).
//
// `(metric [:variant kpi] :span [W H] :value (col …) :label … :format …
//          :delta? (col …) :as-of? (col …))`
//
// `:variant` is a single-member enum (`kpi`) and defaults to `kpi`, so
// callers may omit it. `:value` / `:delta` / `:as-of` are explicit
// `(col …)` refs, evaluated lazily and stored unresolved (V-shape).
// Registered in `dashboardMeta` (NOT `opMeta`) — only reachable as a
// child of `(dashboard …)`.

import { z } from 'zod';

import { colRefArg, spanArg } from '../args.ts';
import { buildSpec, type DashboardCtx, dashboardMeta } from '../dispatch.ts';
import { METRIC_FORMATS, METRIC_VARIANTS } from '../enums.ts';
import type { MetricSpec } from '../value.ts';

export const metric = z
  .object({
    name: z.literal('metric'),
    params: z.tuple([]),
    variant: z.enum(METRIC_VARIANTS).default('kpi'),
    span: spanArg,
    value: colRefArg,
    label: z.string().min(1),
    format: z.enum(METRIC_FORMATS),
    delta: colRefArg.optional(),
    'as-of': colRefArg.optional(),
  })
  .describe(
    'A single-number KPI tile. `:value` is an explicit `(col ds colname)` that MUST resolve to a single row — ' +
      'collapse its dataset with a group-by-less `(aggregate …)` (e.g. `(count)`, `(sum …)`, `(first …)` after a sort) ' +
      'or point at an already-scalar column; binding `:value` to a grouped, multi-row column (one built for a table or ' +
      'ranking) is rejected. `:format` controls rendering; ' +
      'optional `:delta` (col …) drives a change indicator. `:as-of` (col …) is a companion timestamp ' +
      'column whose LAST row is appended to the label as an "as of <date>" stamp — sort the dataset so the last row is the value\'s measurement time.',
  )
  .register(dashboardMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(metric :span [4 2] :value (col wells total) :label "Total" :format compact)' },
    apply: (data, ctx: DashboardCtx): Promise<MetricSpec> =>
      buildSpec('metric-spec', data, ctx, [
        { key: 'variant', mode: 'copy' },
        { key: 'span', mode: 'copy' },
        { key: 'value', mode: 'col' },
        { key: 'label', mode: 'copy' },
        { key: 'format', mode: 'copy' },
        { key: 'delta', mode: 'optional-col' },
        { key: 'asOf', from: 'as-of', mode: 'optional-col' },
      ]),
  });
